import React from "react";
import { useTranslation } from "react-i18next";
import { getThemeName, isInternationalTheme, getThemeEmail } from "config/ConfigUtils";

const TermsOfService = () => {
  const { t } = useTranslation();
  const brand = getThemeName();
  const email = getThemeEmail();
  const country = isInternationalTheme() ? "United States" : "Türkiye";
  const lastUpdated = "2026-02-03";

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -70; // 20px offset from top
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handlePrint = () => {
    // Save original title and set custom PDF filename
    const originalTitle = document.title;
    const pdfFileName = isInternationalTheme() ? `${brand}_Terms_of_Service` : `${brand}_Kullanim_Sartlari`;
    document.title = pdfFileName;

    // Add print styles dynamically
    const printStyles = document.createElement("style");
    printStyles.id = "terms-print-styles";
    printStyles.textContent = `
      @media print {
        body * { visibility: hidden; }
        #terms-container, #terms-container * { visibility: visible; }
        #terms-container { 
          position: absolute; 
          left: 0; 
          top: 0; 
          width: 100%;
          padding: 20px;
        }
        .no-print { display: none !important; }
      }
    `;
    document.head.appendChild(printStyles);
    window.print();
    // Remove print styles and restore title after printing
    setTimeout(() => {
      document.title = originalTitle;
      const existingStyles = document.getElementById("terms-print-styles");
      if (existingStyles) existingStyles.remove();
    }, 1000);
  };

  const styles = {
    container: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "40px 24px",
      backgroundColor: "#ffffff",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      color: "#1a1a1a",
      lineHeight: "1.7",
    },
    header: {
      borderBottom: "1px solid #e5e5e5",
      paddingBottom: "24px",
      marginBottom: "32px",
    },
    title: {
      fontSize: "28px",
      fontWeight: "700",
      marginBottom: "8px",
      color: "#111827",
    },
    lastUpdated: {
      fontSize: "14px",
      color: "#6b7280",
      marginBottom: "16px",
    },
    printButton: {
      fontSize: "14px",
      color: "#3b82f6",
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "0",
      textDecoration: "underline",
    },
    toc: {
      backgroundColor: "#f9fafb",
      border: "1px solid #e5e5e5",
      borderRadius: "8px",
      padding: "20px 24px",
      marginBottom: "32px",
    },
    tocTitle: {
      fontSize: "16px",
      fontWeight: "600",
      marginBottom: "12px",
      color: "#374151",
    },
    tocList: {
      listStyle: "none",
      padding: "0",
      margin: "0",
    },
    tocItem: {
      marginBottom: "8px",
    },
    tocLink: {
      fontSize: "14px",
      color: "#3b82f6",
      textDecoration: "none",
      cursor: "pointer",
    },
    section: {
      marginBottom: "32px",
    },
    sectionTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#111827",
      marginBottom: "16px",
      paddingTop: "8px",
    },
    paragraph: {
      fontSize: "15px",
      marginBottom: "12px",
      color: "#374151",
    },
    list: {
      paddingLeft: "20px",
      marginBottom: "12px",
    },
    listItem: {
      fontSize: "15px",
      marginBottom: "8px",
      color: "#374151",
    },
  };

  const tocItems = [
    { id: "definitions", label: t("terms.toc.definitions") },
    { id: "acceptance", label: t("terms.toc.acceptance") },
    { id: "services", label: t("terms.toc.services") },
    { id: "registration", label: t("terms.toc.registration") },
    { id: "obligations", label: t("terms.toc.obligations") },
    { id: "sms", label: t("terms.toc.sms") },
    { id: "mobile", label: t("terms.toc.mobile") },
    { id: "location", label: t("terms.toc.location") },
    { id: "payment", label: t("terms.toc.payment") },
    { id: "ip", label: t("terms.toc.ip") },
    { id: "liability", label: t("terms.toc.liability") },
    { id: "termination", label: t("terms.toc.termination") },
    { id: "dispute", label: t("terms.toc.dispute") },
    { id: "changes", label: t("terms.toc.changes") },
    { id: "contact", label: t("terms.toc.contact") },
  ];

  return (
    <div id="terms-container" style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>{t("terms.termsOfService", { brand })}</h1>
        <p style={styles.lastUpdated}>
          {t("terms.lastUpdated")}: {lastUpdated}
        </p>
        <button style={styles.printButton} onClick={handlePrint} className="no-print">
          {t("terms.print")}
        </button>
      </div>

      {/* Introduction */}
      <p style={styles.paragraph}>{t("terms.termsOfServiceIntro", { brand })}</p>

      {/* Table of Contents */}
      <div style={styles.toc}>
        <h2 style={styles.tocTitle}>{t("terms.tableOfContents")}</h2>
        <ol style={styles.tocList}>
          {tocItems.map((item, index) => (
            <li key={item.id} style={styles.tocItem}>
              <a
                href={`#${item.id}`}
                style={styles.tocLink}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.id);
                }}
              >
                {index + 1}. {item.label}
              </a>
            </li>
          ))}
        </ol>
      </div>

      {/* 1. Definitions */}
      <div id="definitions" style={styles.section}>
        <h2 style={styles.sectionTitle}>1. {t("terms.toc.definitions")}</h2>
        <p style={styles.paragraph}>{t("terms.definitionsIntro", { brand })}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("terms.definitionPlatform", { brand })}</li>
          <li style={styles.listItem}>{t("terms.definitionUser")}</li>
          <li style={styles.listItem}>{t("terms.definitionServices")}</li>
          <li style={styles.listItem}>{t("terms.definitionContent")}</li>
        </ul>
      </div>

      {/* 2. Acceptance */}
      <div id="acceptance" style={styles.section}>
        <h2 style={styles.sectionTitle}>2. {t("terms.toc.acceptance")}</h2>
        <p style={styles.paragraph}>{t("terms.acceptanceDetails", { brand })}</p>
      </div>

      {/* 3. Services */}
      <div id="services" style={styles.section}>
        <h2 style={styles.sectionTitle}>3. {t("terms.toc.services")}</h2>
        <p style={styles.paragraph}>{t("terms.servicesIntro", { brand })}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("terms.serviceRouteManagement")}</li>
          <li style={styles.listItem}>{t("terms.serviceLiveTracking")}</li>
          <li style={styles.listItem}>{t("terms.serviceSmsNotifications")}</li>
          <li style={styles.listItem}>{t("terms.serviceMobileApp")}</li>
          <li style={styles.listItem}>{t("terms.serviceReporting")}</li>
        </ul>
      </div>

      {/* 4. Registration */}
      <div id="registration" style={styles.section}>
        <h2 style={styles.sectionTitle}>4. {t("terms.toc.registration")}</h2>
        <p style={styles.paragraph}>{t("terms.registrationDetails")}</p>
        <p style={styles.paragraph}>{t("terms.security")}</p>
      </div>

      {/* 5. Obligations */}
      <div id="obligations" style={styles.section}>
        <h2 style={styles.sectionTitle}>5. {t("terms.toc.obligations")}</h2>
        <p style={styles.paragraph}>{t("terms.usageDetails")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("terms.obligationLawful")}</li>
          <li style={styles.listItem}>{t("terms.obligationAccurate")}</li>
          <li style={styles.listItem}>{t("terms.obligationSecurity")}</li>
          <li style={styles.listItem}>{t("terms.obligationNoAbuse")}</li>
        </ul>
      </div>

      {/* 6. SMS */}
      <div id="sms" style={styles.section}>
        <h2 style={styles.sectionTitle}>6. {t("terms.toc.sms")}</h2>
        <p style={styles.paragraph}>{t("terms.smsIntro", { brand })}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("terms.smsServiceAlerts")}</li>
          <li style={styles.listItem}>{t("terms.smsRouteUpdates")}</li>
          <li style={styles.listItem}>{t("terms.smsVerification")}</li>
        </ul>
        <p style={styles.paragraph}>{t("terms.smsConsent")}</p>
        <p style={styles.paragraph}>{t("terms.smsOptOut")}</p>
      </div>

      {/* 7. Mobile App */}
      <div id="mobile" style={styles.section}>
        <h2 style={styles.sectionTitle}>7. {t("terms.toc.mobile")}</h2>
        <p style={styles.paragraph}>{t("terms.mobileIntro")}</p>
        <p style={styles.paragraph}>{t("terms.mobilePermissions")}</p>
      </div>

      {/* 8. Location */}
      <div id="location" style={styles.section}>
        <h2 style={styles.sectionTitle}>8. {t("terms.toc.location")}</h2>
        <p style={styles.paragraph}>{t("terms.locationIntro")}</p>
        <p style={styles.paragraph}>{t("terms.locationUsage")}</p>
      </div>

      {/* 9. Payment */}
      <div id="payment" style={styles.section}>
        <h2 style={styles.sectionTitle}>9. {t("terms.toc.payment")}</h2>
        <p style={styles.paragraph}>{t("terms.paymentIntro")}</p>
        <p style={styles.paragraph}>{t("terms.paymentTerms")}</p>
      </div>

      {/* 10. Intellectual Property */}
      <div id="ip" style={styles.section}>
        <h2 style={styles.sectionTitle}>10. {t("terms.toc.ip")}</h2>
        <p style={styles.paragraph}>{t("terms.ipDetails", { brand })}</p>
      </div>

      {/* 11. Liability */}
      <div id="liability" style={styles.section}>
        <h2 style={styles.sectionTitle}>11. {t("terms.toc.liability")}</h2>
        <p style={styles.paragraph}>{t("terms.liabilityDetails", { brand })}</p>
        <p style={styles.paragraph}>{t("terms.liabilityDisclaimer")}</p>
      </div>

      {/* 12. Termination */}
      <div id="termination" style={styles.section}>
        <h2 style={styles.sectionTitle}>12. {t("terms.toc.termination")}</h2>
        <p style={styles.paragraph}>{t("terms.terminationDetails", { brand })}</p>
      </div>

      {/* 13. Dispute */}
      <div id="dispute" style={styles.section}>
        <h2 style={styles.sectionTitle}>13. {t("terms.toc.dispute")}</h2>
        <p style={styles.paragraph}>{t("terms.disputeLaw", { country })}</p>
        <p style={styles.paragraph}>{t("terms.disputeCourt", { country })}</p>
      </div>

      {/* 14. Changes */}
      <div id="changes" style={styles.section}>
        <h2 style={styles.sectionTitle}>14. {t("terms.toc.changes")}</h2>
        <p style={styles.paragraph}>{t("terms.modification")}</p>
        <p style={styles.paragraph}>{t("terms.effectivenessDetails")}</p>
      </div>

      {/* 15. Contact */}
      <div id="contact" style={styles.section}>
        <h2 style={styles.sectionTitle}>15. {t("terms.toc.contact")}</h2>
        <p style={styles.paragraph}>{t("terms.contactDetails", { brand, email })}</p>
      </div>
    </div>
  );
};

export default TermsOfService;
